import { type ReactNode, useMemo } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppSelector } from "app/hooks";
import { announcement } from "app/themes/colors";
import WordProcessorHtmlContent from "components/word-processor-field/word-processor-html-content.component";
import {
  selectCurrentClinic,
  selectCurrentPractice
} from "features/auth/auth-slice";
import { useGetClinicQuery } from "services/clinics";
import type { EmailBrandingKind } from "services/types/email-branding.type";
import { sanitizeHTML } from "utils/sanitize-html.util";

import {
  EMAIL_BRANDING_FRAME_CLINIC_QUERY,
  type FrameClinic,
  type FrameSender,
  hasFrameParts,
  resolveFrameParts
} from "./email-branding-frame.utils";
import { useCurrentUserFrameSender } from "./use-current-user-frame-sender.hook";
import { useEmailBranding } from "./use-email-branding.hook";

export interface EmailBrandingFrameProps {
  /** "manual" for an email staff write; "any" for a reusable template. */
  kind: EmailBrandingKind | "any" | undefined;
  /** The Designer tab is active: no logo, and nothing at all for a type in no kind. */
  isDesigner: boolean;
  /** False draws no frame (the "Include email branding" box is unticked). */
  enabled?: boolean;
  /**
   * The clinic whose logo, signature and disclaimer the email uses. Omitted: the clinic picked at
   * the top right; null: no clinic, so the practice's.
   */
  clinicId?: number | null;
  /**
   * Who sends it. "current-user": the signed-in staff member (New Email, touchpoints and bulk
   * sends go out as their creator). Omitted or null: staff fields read as placeholders.
   */
  sender?: FrameSender | "current-user" | null;
  /**
   * The frame shows one clinic's branding for an email that is sent from many (a campaign, a
   * template). The tag then says which clinic it is drawn for.
   */
  example?: boolean;
  /** The classic editor's body: a part placed by its merge tag is not drawn again below. */
  body?: string | null;
  children: ReactNode;
}

const FRAME_PART_SX = { px: 2, color: "text.secondary" } as const;

/**
 * Draws the branding the backend adds around an editor: the logo above it, the signature and the
 * disclaimer below it, greyed and read-only. It is drawn around the editor, never inside it, so
 * nothing here can be edited, saved into a template or sent twice. Renders the editor unchanged
 * whenever there is nothing to add.
 */
export default function EmailBrandingFrame(props: EmailBrandingFrameProps) {
  const { isSupported } = useEmailBranding();

  // Without email branding nothing is added, so nothing is read: no clinic request, no store read
  // beyond the hook's own. Support does not change while an editor is open.
  if (!isSupported) return <Box>{props.children}</Box>;
  return <SupportedEmailBrandingFrame {...props} />;
}

function SupportedEmailBrandingFrame({
  kind,
  isDesigner,
  enabled = true,
  clinicId,
  sender = null,
  example = false,
  body,
  children
}: EmailBrandingFrameProps) {
  const { branding, hasSavedSettings } = useEmailBranding();
  const practice = useAppSelector(selectCurrentPractice);
  const currentUserSender = useCurrentUserFrameSender();
  const resolvedSender = sender === "current-user" ? currentUserSender : sender;
  const currentClinic = useAppSelector(selectCurrentClinic);
  // Undefined: the clinic picked at the top right, the one an example is drawn for.
  const effectiveClinicId =
    clinicId === undefined ? currentClinic?.id : clinicId;
  const active = enabled;
  const { currentData: clinic } = useGetClinicQuery(
    {
      clinicId: effectiveClinicId ?? 0,
      query: EMAIL_BRANDING_FRAME_CLINIC_QUERY
    },
    { skip: !active || !effectiveClinicId }
  );

  const parts = useMemo(
    () =>
      resolveFrameParts({
        branding,
        hasSavedSettings,
        kind,
        isDesigner,
        clinic: (clinic as FrameClinic | undefined) ?? null,
        practiceLogo: practice?.logo,
        practiceName: practice?.name ?? "",
        sender: resolvedSender,
        body
      }),
    [
      branding,
      hasSavedSettings,
      kind,
      isDesigner,
      clinic,
      practice?.logo,
      practice?.name,
      resolvedSender,
      body
    ]
  );
  const signatureHtml = useMemo(
    () => (parts.signatureHtml ? sanitizeHTML(parts.signatureHtml) : ""),
    [parts.signatureHtml]
  );

  // The editor is always rendered at the same place in the tree, framed or not: swapping the wrapper
  // in and out would remount TinyMCE or the Designer, and lose an unsaved design.
  const isFramed = active && hasFrameParts(parts);
  const clinicName = clinic?.display_name || clinic?.name;
  const tag = example
    ? clinicName
      ? `Example: as sent from ${clinicName}`
      : "Example: added when sent"
    : "Added when sent";

  return (
    <Box
      data-testid={isFramed ? "email-branding-frame" : undefined}
      sx={
        isFramed
          ? {
              position: "relative",
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "action.hover",
              px: 1.25,
              pt: 2,
              pb: 1.25,
              mt: 1.5
            }
          : undefined
      }
    >
      {isFramed && (
        <Typography
          variant="body1"
          component="span"
          fontWeight={700}
          sx={{
            position: "absolute",
            top: 0,
            right: 12,
            transform: "translateY(-50%)",
            px: 1.25,
            py: 0.25,
            borderRadius: 999,
            bgcolor: announcement.background,
            color: announcement.text,
            border: "1px solid",
            borderColor: announcement.border
          }}
        >
          {tag}
        </Typography>
      )}

      {isFramed && parts.logoUrl && (
        <Box
          aria-hidden
          sx={{ display: "flex", justifyContent: "center", pb: 1.25 }}
        >
          <Box
            component="img"
            src={parts.logoUrl}
            alt=""
            data-testid="email-branding-frame-logo"
            sx={{ maxHeight: 64, maxWidth: "60%", opacity: 0.85 }}
          />
        </Box>
      )}

      <Box>{children}</Box>

      {isFramed && (signatureHtml || parts.disclaimerText) && (
        <Stack gap={1} sx={{ pt: 1.25 }} aria-label="Added when sent">
          {signatureHtml && (
            <WordProcessorHtmlContent
              html={signatureHtml}
              dataTestId="email-branding-frame-signature"
              sx={{ ...FRAME_PART_SX, opacity: 0.85, m: 0 }}
            />
          )}
          {parts.disclaimerText && (
            <Typography
              variant="body1"
              data-testid="email-branding-frame-disclaimer"
              sx={{
                ...FRAME_PART_SX,
                whiteSpace: "pre-line",
                borderTop: signatureHtml ? "1px solid" : undefined,
                borderColor: "divider",
                pt: signatureHtml ? 1 : 0
              }}
            >
              {parts.disclaimerText}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}
